import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * A reusable caret icon component for dropdown indicators.
 * Displays a downward-pointing chevron that can rotate when open.
 * Supports custom rotation angles for open and closed states.
 *
 * @example
 * Basic usage (default: down to up):
 * ```html
 * <app-caret-icon
 *   [isOpen]="dropdownOpen"
 *   [customClass]="'my-caret'">
 * </app-caret-icon>
 * ```
 *
 * @example
 * Custom rotation (right to down):
 * ```html
 * <app-caret-icon
 *   [isOpen]="isExpanded"
 *   [closedRotation]="-90"
 *   [openRotation]="0">
 * </app-caret-icon>
 * ```
 */
@Component({
  selector: 'app-caret-icon',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './caret-icon.component.html',
  styleUrls: ['./caret-icon.component.scss'],
})
export class CaretIconComponent {
  /**
   * Whether the caret should be in the "open" state (rotated 180deg)
   */
  isOpen = input<boolean>(false);

  /**
   * Optional custom CSS class to apply to the SVG element
   */
  customClass = input<string>('');

  /**
   * Width of the icon (default: 16)
   */
  width = input<number>(16);

  /**
   * Height of the icon (default: 16)
   */
  height = input<number>(16);

  /**
   * Color of the caret (CSS color value)
   * If not provided, inherits from parent
   */
  color = input<string>('');

  /**
   * Rotation angle (in degrees) when the caret is closed
   * Default: 0 (points down)
   */
  closedRotation = input<number>(0);

  /**
   * Rotation angle (in degrees) when the caret is open
   * Default: 180 (points up)
   */
  openRotation = input<number>(-180);
}
